import { Box, Collapse, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  isAgentEntry,
  NotificationEntry,
  NotificationStatus,
  SignalNotificationLog,
} from '@provision/signal-notification-log';
import React, { useState } from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { useDisplayTime } from 'src/utils/hooks';
import {
  attemptProgress,
  canceledCount,
  channelLabel,
  countdownLabel,
  entryDetail,
  entryWho,
  hasNoOnCallSchedule,
  historyEntries,
  isExhausted,
  nextUp,
  NO_ON_CALL_SCHEDULE_LABEL,
  scheduledEntries,
  secondsUntil,
  sentCount,
} from './notification-log.helpers';
import { useSecondTick } from './use-second-tick';

const overlineTextSx: SxProps<Theme> = {
  fontSize: (theme) => theme.typography.overline.fontSize,
  lineHeight: '1.125rem',
};

const mutedSx: SxProps<Theme> = {
  ...overlineTextSx,
  color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
};

const toggleSx: SxProps<Theme> = {
  ...mutedSx,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mt: 1,
  cursor: 'pointer',
  userSelect: 'none',
  width: 'fit-content',
  '&:hover': { color: (theme) => theme.palette.custom.OV.sys.onSurface },
};

const iconSx: SxProps<Theme> = {
  fontSize: '0.8125rem',
  width: '0.8125rem',
  height: '0.8125rem',
};

const ActiveIndicator = (): React.JSX.Element => {
  const theme = useTheme();

  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Box
        sx={{
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '50%',
          backgroundColor: theme.palette.error.main,
          '@media (prefers-reduced-motion: no-preference)': {
            animation: 'signalPulse 2s infinite',
          },
          '@keyframes signalPulse': {
            '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.55)}` },
            '70%': { boxShadow: `0 0 0 0.5rem ${alpha(theme.palette.error.main, 0)}` },
            '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}` },
          },
        }}
      />
      <Typography
        sx={{ ...overlineTextSx, fontWeight: 700, letterSpacing: '0.09em' }}
        color="error"
      >
        ACTIVE
      </Typography>
    </Stack>
  );
};

/** Mounted only while unresolved, which keeps the shared ticker off for resolved incidents. */
const NextUpLine = ({ log }: { log: SignalNotificationLog }): React.JSX.Element | null => {
  const theme = useTheme();
  const now = useSecondTick();
  const next = nextUp(log);
  const { current, total } = attemptProgress(log);

  if (isExhausted(log)) {
    return (
      <Stack direction="row" gap={0.75} alignItems="center" mt={1}>
        <DynamicIcon name="schedule" sx={{ ...iconSx, color: theme.palette.warning.main }} />
        <Typography sx={overlineTextSx} color={theme.palette.warning.main}>
          {`All ${total} attempts sent`}
        </Typography>
      </Stack>
    );
  }

  if (!next) return null;

  const seconds = secondsUntil(next.at, now);
  const who = entryWho(next);
  const detail = isAgentEntry(next) ? `${channelLabel(next.channel)} to ` : '';

  return (
    <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap" mt={1}>
      <Stack direction="row" gap={0.75} alignItems="center" flex={1} minWidth={0}>
        <DynamicIcon name="schedule" sx={{ ...iconSx, color: theme.palette.warning.main }} />
        <Typography sx={overlineTextSx} color={theme.palette.warning.main}>
          {`Next: ${detail}`}
          <strong>{who}</strong>
          {seconds === null ? '' : ` · ${countdownLabel(seconds)}`}
        </Typography>
      </Stack>
      <Typography sx={mutedSx}>{`attempt ${current} of ${total}`}</Typography>
    </Stack>
  );
};

/**
 * Entries have no id; key on content so React can't reuse a sent row's DOM as the tail is replaced.
 * `scheduleId` matters: the same user on call for two schedules produces two otherwise-identical
 * entries in one iteration.
 */
const entryKey = (entry: NotificationEntry): string =>
  [
    entry.status,
    entry.at,
    (entry as { iteration?: number }).iteration,
    (entry as { scheduleId?: string }).scheduleId,
    entryWho(entry),
    entryDetail(entry),
  ].join('|');

type RowVariant = 'history' | 'next' | 'planned';

const EntryRow = ({
  entry,
  variant,
}: {
  entry: NotificationEntry;
  variant: RowVariant;
}): React.JSX.Element => {
  const theme = useTheme();
  const { displayTime } = useDisplayTime();
  const failed = entry.status === NotificationStatus.Failed;

  // sent = green check, failed = amber warning, next = amber clock, later planned = dimmed dot.
  let icon = 'check_circle';
  let color: string = theme.palette.custom.OV.sys.success;

  if (failed) {
    icon = 'warning';
    color = theme.palette.warning.main;
  } else if (variant === 'next') {
    icon = 'schedule';
    color = theme.palette.warning.main;
  } else if (variant === 'planned') {
    icon = 'fiber_manual_record';
    color = theme.palette.custom.OV.sys.onSurfaceVariant;
  }

  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      sx={{
        opacity: variant === 'planned' ? 0.62 : 1,
        minWidth: 0,
        ...(variant === 'next' && {
          backgroundColor: alpha(theme.palette.warning.main, 0.1),
          borderRadius: '0.375rem',
          px: 0.5,
          mx: -0.5,
        }),
      }}
    >
      <DynamicIcon name={icon} sx={{ ...iconSx, color }} />
      <Typography sx={{ ...mutedSx, minWidth: '2.75rem' }}>
        {entry.at ? displayTime(entry.at) : ''}
      </Typography>
      <Typography sx={{ ...overlineTextSx, flex: 1, minWidth: 0 }} noWrap>
        {entryWho(entry)}
      </Typography>
      <Typography sx={mutedSx} noWrap>
        {entryDetail(entry)}
      </Typography>
    </Stack>
  );
};

interface Props {
  log?: SignalNotificationLog | null;
  isResolved: boolean;
}

/** One shared preference, not per incident: remember whether the user wants the log open. */
const EXPANDED_STORAGE_KEY = 'signal-incident-notification-log-expanded';

const readStoredExpanded = (): boolean => {
  try {
    // Default open: only an explicit collapse is remembered as closed.
    return window.localStorage.getItem(EXPANDED_STORAGE_KEY) !== 'collapsed';
  } catch {
    return true;
  }
};

const storeExpanded = (expanded: boolean): void => {
  try {
    window.localStorage.setItem(EXPANDED_STORAGE_KEY, expanded ? 'expanded' : 'collapsed');
  } catch {
    // Storage unavailable (private mode, quota) — the preference just isn't remembered.
  }
};

/** No log → render nothing (legacy incident or invalid payload), falling back to prior behavior. */
export const SignalIncidentNotificationLog = ({
  log,
  isResolved,
}: Props): React.JSX.Element | null => {
  const [expanded, setExpanded] = useState(readStoredExpanded);

  const toggleExpanded = (): void => {
    const next = !expanded;
    setExpanded(next);
    storeExpanded(next);
  };

  if (!log || log.notifications.length === 0) {
    return null;
  }

  // No schedule → partner-only log. Keep the ACTIVE pulse; skip timeline / attempts / countdown.
  if (hasNoOnCallSchedule(log)) {
    return (
      <Stack mt={1} data-testid="signal-no-on-call-schedule">
        {!isResolved ? <ActiveIndicator /> : null}
        <Typography sx={{ ...mutedSx, mt: isResolved ? 0 : 1 }}>
          {NO_ON_CALL_SCHEDULE_LABEL}
        </Typography>
      </Stack>
    );
  }

  const history = historyEntries(log);
  const scheduled = scheduledEntries(log);
  // Derived: leftover forecast on a resolved incident is what never fired.
  const canceled = canceledCount(log);

  const [next, ...plannedRows] = isResolved ? [] : scheduled;

  return (
    <Stack mt={1}>
      {isResolved ? (
        <Stack direction="row" gap={0.75} alignItems="center">
          <DynamicIcon
            name="check_circle"
            sx={{ ...iconSx, color: (theme: Theme) => theme.palette.custom.OV.sys.success }}
          />
          <Typography sx={mutedSx}>
            {`${sentCount(log)} notification${sentCount(log) === 1 ? '' : 's'} sent · ${canceled} canceled`}
          </Typography>
        </Stack>
      ) : (
        <>
          <ActiveIndicator />
          <NextUpLine log={log} />
        </>
      )}

      <Typography
        sx={toggleSx}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        data-testid="signal-notification-log-toggle"
        onClick={toggleExpanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') toggleExpanded();
        }}
      >
        <DynamicIcon name={expanded ? 'expand_more' : 'chevron_right'} sx={iconSx} />
        {isResolved ? 'Notification history' : 'Notification history & plan'}
      </Typography>

      <Collapse in={expanded} unmountOnExit>
        <Stack
          gap={0.5}
          mt={1}
          pt={1.25}
          sx={{
            borderTop: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
          }}
          data-testid="signal-notification-log-body"
        >
          {history.map((entry) => (
            <EntryRow key={entryKey(entry)} entry={entry} variant="history" />
          ))}
          {next ? <EntryRow key={entryKey(next)} entry={next} variant="next" /> : null}
          {plannedRows.map((entry) => (
            <EntryRow key={entryKey(entry)} entry={entry} variant="planned" />
          ))}
          {isResolved && canceled > 0 ? (
            <Stack direction="row" gap={0.75} alignItems="center" mt={0.5}>
              <DynamicIcon
                name="check_circle"
                sx={{
                  ...iconSx,
                  color: (theme: Theme) => theme.palette.custom.OV.sys.success,
                }}
              />
              <Typography sx={mutedSx}>
                {`Resolved — ${canceled} remaining attempt${canceled === 1 ? '' : 's'} canceled`}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Collapse>
    </Stack>
  );
};
